import type { IReportSection, ISectionData } from 'src/types';
import {
  AiActionKind,
  AiNarrativeContext,
  AiPromptStatusResponse,
  AiPromptTemplate,
  AiSourceReference,
  AiTemplateAction,
} from './types';

export function getPlainText(html: string): string {
  if (!html) return '';

  const spaced = html.replace(
    /<\/?(p|div|li|ul|ol|h[1-6]|br|blockquote|pre|tr|td|th)[^>]*>/gi,
    ' ',
  );
  const div = document.createElement('div');
  div.innerHTML = spaced;
  return div.textContent || '';
}

const PLACEHOLDER_PATTERN = /\{\{\s*(\w+)\s*\}\}|\{\s*(\w+)\s*\}/g;

export function fillTemplate(
  template: string,
  vars: Record<string, string | number>,
): string {
  const missing: string[] = [];

  const filled = template.replace(
    PLACEHOLDER_PATTERN,
    (match, doubled, single) => {
      const name = doubled || single;
      const value = vars[name];

      if (value === undefined || value === null) {
        missing.push(name);
        return match;
      }

      return String(value);
    },
  );

  if (missing.length) {
    throw new Error(
      `Prompt template has unresolved placeholders: ${Array.from(
        new Set(missing),
      ).join(', ')}`,
    );
  }

  return filled;
}

interface ApiResponse {
  status?: number;
  data?: unknown;
}

export function readApiResponse(
  response: ApiResponse,
  request: string,
): unknown {
  if (!response) {
    throw new Error(`${request}: no response received`);
  }

  const status = response.status ?? 0;
  if (status < 200 || status >= 300) {
    throw new Error(`${request}: request failed with status ${status}`);
  }

  return response.data;
}

const TEMPLATE_LIST_FIELD = 'prompt_templates';

export function asTemplateList(data: unknown): AiPromptTemplate[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (!data || typeof data !== 'object') {
    return [];
  }

  const list = (data as Record<string, unknown>)[TEMPLATE_LIST_FIELD];
  if (Array.isArray(list)) {
    return list;
  }

  return [data as Record<string, unknown>];
}

const ACTION_FIELD = 'ai_action';

const ACTION_LABEL_FIELD = 'action_label';

const TEMPLATE_TEXT_FIELD = 'prompt';

const GENERATED_TEXT_FIELD = 'response';

const USED_DOCUMENTS_FIELD = 'used_documents';

const PROMPT_TYPE_FIELD = 'prompt_type';

// Celery task states: the payload carries a task_id and PENDING/SUCCESS, so the
// remaining states are its own.
const FAILED_STATUSES = ['failure', 'revoked'];

const PENDING_STATUSES = ['pending', 'received', 'started', 'retry'];

function readString(
  source: Record<string, unknown>,
  field: string,
): string | null {
  const value = source[field];
  return typeof value === 'string' && value.trim() ? value : null;
}

export function readTemplateActions(items: AiPromptTemplate[]): {
  primary: AiTemplateAction[];
  contextual: AiTemplateAction[];
} {
  const primary: AiTemplateAction[] = [];
  const contextual: AiTemplateAction[] = [];

  (items || []).forEach(item => {
    const action = readString(item, ACTION_FIELD);
    const label = readString(item, ACTION_LABEL_FIELD);
    const promptType = readString(item, PROMPT_TYPE_FIELD);
    if (!action || !label) {
      return;
    }
    if (promptType === 'primary') {
      primary.push({ action, label });
    } else if (promptType === 'contextual') {
      contextual.push({ action, label });
    }
  });

  return { primary, contextual };
}

export function pickTemplate(
  items: AiPromptTemplate[],
  actionKind: AiActionKind,
): string {
  const list = items || [];
  const match = list.find(
    item => readString(item, ACTION_FIELD) === actionKind,
  );

  if (!match) {
    const seen = list
      .map(
        item =>
          readString(item, ACTION_FIELD) || `{${Object.keys(item).join(',')}}`,
      )
      .join(', ');
    throw new Error(
      `No AI prompt template for "${actionKind}" (available: ${seen || 'none'})`,
    );
  }

  const text = readString(match, TEMPLATE_TEXT_FIELD);
  if (!text) {
    throw new Error(
      `AI prompt template "${actionKind}" carries no ${TEMPLATE_TEXT_FIELD} (fields: ${Object.keys(
        match,
      ).join(', ')})`,
    );
  }
  return text;
}

function findSectionPath(
  sections: IReportSection[],
  activeSectionId: number,
  trail: IReportSection[] = [],
): IReportSection[] | null {
  for (const section of sections || []) {
    const path = [...trail, section];
    if (section.section_pk === activeSectionId) {
      return path;
    }
    const found = section.children_list
      ? findSectionPath(section.children_list, activeSectionId, path)
      : null;
    if (found) {
      return found;
    }
  }
  return null;
}

export function readSectionNames(
  sections: IReportSection[],
  activeSectionId: number,
): { sectionName: string; subsectionName: string } {
  const path = findSectionPath(sections, activeSectionId) || [];
  return {
    sectionName: path[0]?.current_title || '',
    subsectionName:
      path.length > 1 ? path[path.length - 1].current_title || '' : '',
  };
}

function clean(value: unknown): unknown {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (Array.isArray(value)) {
    const cleaned = value.map(clean).filter(item => item !== undefined);
    return cleaned.length > 0 ? cleaned : undefined;
  }

  if (typeof value === 'object' && value.constructor === Object) {
    const cleaned: Record<string, unknown> = {};
    let hasKeys = false;

    Object.entries(value).forEach(([key, entry]) => {
      const cleanedEntry = clean(entry);
      if (cleanedEntry !== undefined) {
        cleaned[key] = cleanedEntry;
        hasKeys = true;
      }
    });

    return hasKeys ? cleaned : undefined;
  }

  return value;
}

export function sanitizeAndMinifyJson(data: unknown): string {
  return JSON.stringify(clean(data) ?? {});
}

export function buildSourceData(
  section: ISectionData,
  dataKeyMap: Record<string, string | string[]>,
): string {
  if (!section) {
    return '';
  }

  const datasets: Record<string, unknown> = {};
  (section.data_sources || []).forEach(({ id, title }) => {
    const slug = dataKeyMap[id];
    if (!slug) {
      return;
    }
    const value = Array.isArray(slug)
      ? slug.reduce(
          (acc, key) => ({ ...acc, [key]: section.datasets?.[key] }),
          {} as Record<string, unknown>,
        )
      : section.datasets?.[slug];

    if (value !== undefined && value !== null) {
      datasets[title || id] = value;
    }
  });

  return sanitizeAndMinifyJson(datasets);
}

export function promptVariables(
  context: AiNarrativeContext,
  extras: { currentContent?: string; selectedText?: string } = {},
): Record<string, string | number> {
  const plainContent = getPlainText(extras.currentContent || '');

  const variables: Record<string, string | number> = {
    narrative_content: plainContent,
    current_content: plainContent,
    subsection_name: context.subsectionName || '',
    source_data: context.sourceData || '',
  };

  const optional: Record<string, string | number | undefined> = {
    csp_code: context.cspCode,
    country_code: context.countryCode,
    country_name: context.countryName,
    year: context.year,
    section_name: context.sectionName,
    narrative_name: context.narrativeName,
    word_count_limit: context.wordCountLimit,
    narrative_type: context.narrativeType,
  };

  Object.entries(optional).forEach(([name, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      variables[name] = value;
    }
  });

  if (extras.selectedText !== undefined) {
    variables.text = extras.selectedText;
    variables.selected_text = extras.selectedText;
  }

  return variables;
}

function statusIn(status: unknown, values: string[]): boolean {
  return (
    typeof status === 'string' && values.includes(status.trim().toLowerCase())
  );
}

export function isFailedStatus(status: unknown): boolean {
  return statusIn(status, FAILED_STATUSES);
}

export function isPendingStatus(status: unknown): boolean {
  return statusIn(status, PENDING_STATUSES);
}

export function readGeneratedText(
  payload: AiPromptStatusResponse,
): string | null {
  return payload ? readString(payload, GENERATED_TEXT_FIELD) : null;
}

export function readUsedDocuments(
  payload: AiPromptStatusResponse,
): AiSourceReference[] {
  const documents = payload?.[USED_DOCUMENTS_FIELD];
  if (!Array.isArray(documents)) {
    return [];
  }

  const references: AiSourceReference[] = [];
  documents.forEach((document, index) => {
    if (!document || typeof document !== 'object') {
      return;
    }
    const record = document as Record<string, unknown>;
    const filename = readString(record, 'filename');
    if (!filename) {
      return;
    }
    const location =
      readString(record, 'presigned_url') || readString(record, 'url');
    references.push({
      id: `${filename}-${index}`,
      title: filename,
      filename,
      url: location && /^https?:\/\//i.test(location) ? location : undefined,
    });
  });

  return references;
}
